<script lang='ts'>
export default {
  name: 'CompaniesStorePostsIndexPostStoresModal',
}
</script>


<script setup lang='ts'>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { KkModal, KkButton, KkLoading } from '../../base'
import { KkStatusMessage } from '../../domains/gbp'
import { basicDateTimeFormat } from '../../../modules/date'

const { t } = useI18n()
interface Emits {
  (event: 'close'): void
}
const emit = defineEmits<Emits>()

const postStores = computed<PostStore[]>(() => {
  const storeUuids = Array.from(new Set([
    ...props.googleMapStores.map(s => s.uuid),
    ...props.docomoStores.map(s => s.uuid),
    ...props.yahooMapStores.map(s => s.uuid),
  ]))

  const googleMapStoreHash = props.googleMapStores.reduce<{ [uuid: string]: GoogleMapStore }>((hash, store) => {
    hash[store.uuid] = store
    return hash
  }, {})

  const docomoStoreHash = props.docomoStores.reduce<{ [uuid: string]: DocomoStore }>((hash, store) => {
    hash[store.uuid] = store
    return hash
  }, {})

  const yahooMapStoreHash = props.yahooMapStores.reduce<{ [uuid: string]: YahooMapStore }>((hash, store) => {
    hash[store.uuid] = store
    return hash
  }, {})

  return storeUuids.map(storeUuid => {
    const googleMapStore = googleMapStoreHash[storeUuid]
    const docomoStore = docomoStoreHash[storeUuid]
    const yahooMapStore = yahooMapStoreHash[storeUuid]

    const services: ('googleMap' | 'docomo' | 'yahooMap')[] = []
    if (googleMapStore) services.push('googleMap')
    if (docomoStore) services.push('docomo')
    if (yahooMapStore) services.push('yahooMap')

    return {
      uuid: storeUuid,
      name: googleMapStore?.name || docomoStore?.name || yahooMapStore?.name || '',
      status: googleMapStore?.status,
      services: services,
      googleMapStore: googleMapStore,
      docomoStore: docomoStore,
      yahooMapStore: yahooMapStore
    }
  })
})

type Status = 'error' | 'progress' | 'complete' | 'closed'

interface PostStore {
  uuid: string
  name: string
  status?: Status
  services:  ('googleMap' | 'docomo' | 'yahooMap')[]
  googleMapStore?: GoogleMapStore
  docomoStore?: DocomoStore
  yahooMapStore?: YahooMapStore
}

interface GoogleMapStore {
  uuid: string
  name: string
  publishedAt: string | null
  status: Status
}

interface DocomoStore {
  uuid: string
  name: string
  publishedAt: string | null
}

interface YahooMapStore {
  uuid: string
  name: string
  publishedAt: string | null
  status: Status
}

interface Props {
  googleMapStores: GoogleMapStore[]
  docomoStores: DocomoStore[]
  yahooMapStores: YahooMapStore[]
  loading: boolean
  isVisible: boolean
  displayResult: boolean
  isPostToDocomoBrandPage?: boolean
}
const props = defineProps<Props>()
</script>

<template>
  <KkModal :is-visible="props.isVisible" width="1076px" @change="emit('close')">
    <div class="p-modal__content__header">
      {{ t('pages.CompaniesStorePost.postStores.title') }}
    </div>
    <div class="p-modal__content__body">
      <KkLoading v-if="loading" />
      <div v-else class="p-table">
        <div class="p-table__content">
          <table>
            <thead>
              <tr>
                <th>{{ t('pages.CompaniesStorePost.postStores.storeName') }}</th>
                <th>{{ t('pages.CompaniesStorePost.postStores.service') }}</th>
                <template v-if="props.displayResult">
                  <th>{{ t('pages.CompaniesStorePost.postStores.status') }}</th>
                  <th>{{ t('pages.CompaniesStorePost.postStores.publishedAt') }}</th>
                </template>
              </tr>
              <tr class="p-table__content__space">
                <td :colspan="props.displayResult ? 4 : 2" />
              </tr>
            </thead>
            <tbody>
              <template v-for="(postStore, postStoreIndex) in postStores" :key="postStore.uuid">
                <tr v-for="(service, serviceIndex) in postStore.services" :key="`${postStore.uuid}_${service}`">
                  <td v-if="serviceIndex === 0" :rowspan="postStore.services.length" class="name" :class="{ 'error': postStore.status === 'error', 'combined-last': postStoreIndex === postStores.length -1 }">
                    {{ postStore.name }}
                  </td>
                  <!-- google map 投稿 -->
                  <template v-if="service === 'googleMap' && postStore.googleMapStore">
                    <td class="service" :class="{ 'error': postStore.status === 'error' }">{{ t(`pages.CompaniesStorePost.postStores.services.${service}`) }}</td>
                    <template v-if="props.displayResult">
                      <td class="status">
                        <KkStatusMessage :status="postStore.googleMapStore.status" />
                      </td>
                      <td class="date">{{ postStore.googleMapStore.publishedAt ? basicDateTimeFormat(postStore.googleMapStore.publishedAt) : '-' }}</td>
                    </template>
                  </template>
                  <!-- docomo 投稿 -->
                  <template v-if="service === 'docomo' && postStore.docomoStore">
                    <td class="service">
                      {{ t(`pages.CompaniesStorePost.postStores.services.${service}`) }}
                      <p v-if="isPostToDocomoBrandPage">{{ t('pages.CompaniesStorePost.postStores.services.docomoBrand') }}</p>
                    </td>
                    <template v-if="props.displayResult">
                      <td class="status">
                        {{ t('pages.CompaniesStorePost.postStores.docomoStatus') }}
                      </td>
                      <td class="date">-</td>
                    </template>
                  </template>
                  <!-- Yahoo! MAP 投稿 -->
                  <template v-if="service === 'yahooMap' && postStore.yahooMapStore">
                    <td class="service" :class="{ 'error': postStore.status === 'error' }">{{ t(`pages.CompaniesStorePost.postStores.services.${service}`) }}</td>
                    <template v-if="props.displayResult">
                      <td class="status">
                        <KkStatusMessage :status="postStore.yahooMapStore.status" />
                      </td>
                      <td class="date">{{ postStore.yahooMapStore.publishedAt ? basicDateTimeFormat(postStore.yahooMapStore.publishedAt) : '-' }}</td>
                    </template>
                  </template>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div class="p-modal__content__footer">
      <div class="c-button c-button--right">
        <KkButton
          color="secondary"
          outlined
          :text="t('labels.close')"
          @click="emit('close')"
        />
      </div>
    </div>
  </KkModal>
</template>

<style lang="scss" scoped>
@import '../../../../../stylesheets/biz_spa/foundation/_var.scss';

.p-table {
  margin-top: 0;
  height: 100%;
}
.p-table__content {
  height: 100%;
  table {
    overflow-y: auto;
  }
  th {
    top: 0;
    position: sticky;
    // NOTE: スタッキングコンテキスト対応 filter, opacityをtd内で利用しているため追加
    z-index: 1;
  }

  .combined-last {
    border-bottom: 1px solid #e4e7eb;
  }

  .name {
    background-color: $color-base-i;
  }

  .service {
    background-color: $color-base-i;
  }

  .date {
    text-align: right;
  }
}

.error {
  color: $color-accent-primary;
}
</style>
